import { Horse, HorseWithNewWeight, HorseWithCost, OwnerCost } from './types';

/**
 * Calculate the sum of weights of all animals
 */
export const summeDerGewichte = (horses: Horse[]): number => {
  return horses.reduce((sum, horse) => sum + parseFloat(String(horse.weight)), 0);
};

/**
 * Calculate the number of necessary packages to buy
 * Formula: x = ceil(Summe(G) / Wg)
 */
export const packagesToBuy = (horses: Horse[], kgProPack: number): number => {
  return Math.ceil(summeDerGewichte(horses) / kgProPack);
};

/**
 * Calculate the amount of weight that could have been served with the bought packages
 * but cannot be used because of the weights of the individual animals.
 * Formula: Rest = x * Wg - Summe(G)
 */
export const notUsed = (horses: Horse[], kgProPack: number): number => {
  return packagesToBuy(horses, kgProPack) * kgProPack - summeDerGewichte(horses);
};

/**
 * The not used kg have to be distributed among the animals.
 * This function calculates a new weight for each animal.
 * After that the total weight of all animals fits to the total amount of packages.
 * Formula: G = (1 + Rest / Summe(G)) * G
 */
export const newWeight = (horses: Horse[], kgProPack: number): HorseWithNewWeight[] => {
  const totalWeight = summeDerGewichte(horses);
  const factor = 1 + notUsed(horses, kgProPack) / totalWeight;

  return horses.map((horse) => ({
    ...horse,
    neuesGewicht: horse.weight * factor
  }));
};

/**
 * Calculate the cost per animal
 * Formula: €2 = (G * Wp / Wg + L) * (1 + St)
 * Where:
 * - G = neuesGewicht
 * - Wp = preisProPack
 * - Wg = kgProPack
 * - L = lohnProTier
 * - St = 19% (tax rate)
 */
export const costPerAnimal = (
  horsesWithNewWeight: HorseWithNewWeight[],
  kgProPack: number,
  preisProPack: number,
  lohnProTier: number
): HorseWithCost[] => {
  return horsesWithNewWeight.map((horse) => {
    let euro = horse.neuesGewicht / kgProPack;  // How many packages do I need for this animal
    euro = euro * preisProPack;                  // How much does it cost
    euro = euro + lohnProTier;                   // Add the work
    euro = euro * 1.19;                          // Add the tax (19%)
    euro = Math.round(euro * 10) / 10;           // Round to 10 Cent values

    return {
      ...horse,
      wkPreis: formatToEuro(euro)
    };
  });
};

/**
 * Take the cost per animal and group by owner
 * Formula: €3 B = Summe(€2 B)
 */
export const costPerOwner = (costPerAnimal: HorseWithCost[]): OwnerCost[] => {
  const ownerMap = new Map<string, number>();

  costPerAnimal.forEach((horse) => {
    const currentCost = ownerMap.get(horse.owner) || 0;
    const horseCost = parseFloat(horse.wkPreis.replace(' €', '').replace(',', '.'));
    ownerMap.set(horse.owner, currentCost + horseCost);
  });

  const result: OwnerCost[] = [];
  ownerMap.forEach((cost, owner) => {
    result.push({
      name: owner,
      preis: formatToEuro(cost)
    });
  });

  return result;
};

/**
 * Calculate the sum of all costs
 */
export const sumOfCost = (costPerAnimal: HorseWithCost[]): string => {
  const sum = costPerAnimal.reduce((total, horse) => {
    const cost = parseFloat(horse.wkPreis.replace(' €', '').replace(',', '.'));
    return total + cost;
  }, 0);

  return formatToEuro(sum);
};

/**
 * Format a number to Euro format with 2 decimals
 */
export const formatToEuro = (value: number): string => {
  return value.toFixed(2).replace('.', ',') + ' €';
};